import type { AgentReleaseChannel, AgentReleaseVersion, AgentUpdateLevel } from './api';

/**
 * Versão do agente de impressão no backoffice (change print-agent).
 *
 * Espelha o `AgentVersion` da API (`VaptMenu.Modules.Printing.Contracts`): formato
 * `major.minor.patch[-pré-release]` e precedência do SemVer 2.0, em que
 * `1.0.57-staging < 1.0.57 < 1.0.58-staging`. Versão fora do formato, como `dev-local`,
 * não se compara e vale como desconhecida — o computador não recebe aviso.
 *
 * A tela usa a comparação só para a prévia do impacto antes de salvar; quem decide o nível
 * de cada computador continua sendo a API.
 */

export interface ParsedAgentVersion {
  major: number;
  minor: number;
  patch: number;
  /** Identificadores do pré-release, sem o hífen; nulo sem pré-release. */
  preRelease: string[] | null;
}

const MAX_LENGTH = 128;
const NUMBER = /^(0|[1-9]\d*)$/;
const IDENTIFIER = /^[0-9A-Za-z-]+$/;

export function parseAgentVersion(text: string | null | undefined): ParsedAgentVersion | null {
  if (!text || text.length > MAX_LENGTH) return null;

  const dash = text.indexOf('-');
  const core = dash >= 0 ? text.slice(0, dash) : text;
  const pre = dash >= 0 ? text.slice(dash + 1) : null;

  const parts = core.split('.');
  if (parts.length !== 3 || !parts.every((p) => NUMBER.test(p))) return null;
  const [major, minor, patch] = parts.map(Number);
  if (![major, minor, patch].every((n) => Number.isSafeInteger(n) && n <= 2_147_483_647)) return null;

  let preRelease: string[] | null = null;
  if (pre !== null) {
    const identifiers = pre.split('.');
    const valid = identifiers.every(
      (id) => id.length > 0 && IDENTIFIER.test(id) && !(/^\d+$/.test(id) && id.length > 1 && id[0] === '0'),
    );
    if (!valid) return null;
    preRelease = identifiers;
  }

  return { major, minor, patch, preRelease };
}

function compareIdentifier(left: string, right: string): number {
  const leftNumeric = /^\d+$/.test(left);
  const rightNumeric = /^\d+$/.test(right);
  if (leftNumeric && rightNumeric) {
    // Sem zero à esquerda, o número mais longo é o maior; do mesmo tamanho, compara os dígitos.
    if (left.length !== right.length) return left.length < right.length ? -1 : 1;
    return left === right ? 0 : left < right ? -1 : 1;
  }
  // Identificador numérico tem precedência menor que o alfanumérico.
  if (leftNumeric !== rightNumeric) return leftNumeric ? -1 : 1;
  // Ordem ordinal, como o `SequenceCompareTo` da API.
  return left === right ? 0 : left < right ? -1 : 1;
}

export function compareParsedVersions(a: ParsedAgentVersion, b: ParsedAgentVersion): number {
  if (a.major !== b.major) return a.major < b.major ? -1 : 1;
  if (a.minor !== b.minor) return a.minor < b.minor ? -1 : 1;
  if (a.patch !== b.patch) return a.patch < b.patch ? -1 : 1;

  // Versão com pré-release vem antes da mesma versão sem ele.
  if (a.preRelease === null && b.preRelease === null) return 0;
  if (a.preRelease === null) return 1;
  if (b.preRelease === null) return -1;

  const length = Math.min(a.preRelease.length, b.preRelease.length);
  for (let i = 0; i < length; i++) {
    const result = compareIdentifier(a.preRelease[i], b.preRelease[i]);
    if (result !== 0) return result;
  }
  // Com os identificadores anteriores iguais, o conjunto maior tem precedência.
  return a.preRelease.length === b.preRelease.length ? 0 : a.preRelease.length < b.preRelease.length ? -1 : 1;
}

/** Compara duas versões; nulo quando alguma está fora do formato. */
export function compareAgentVersions(a: string | null | undefined, b: string | null | undefined): number | null {
  const left = parseAgentVersion(a);
  const right = parseAgentVersion(b);
  if (!left || !right) return null;
  return compareParsedVersions(left, right);
}

function isBelow(version: ParsedAgentVersion, bound: string | null): boolean {
  const parsed = parseAgentVersion(bound);
  return parsed !== null && compareParsedVersions(version, parsed) < 0;
}

/**
 * O nível que a política daria a um computador, como a API calcula (`AgentUpdateLevels`):
 * `mandatory` abaixo da mínima, ou com o protocolo recusado e uma versão-alvo (a recomendada,
 * ou a mínima sem recomendada) maior que a dele; `recommended` abaixo da recomendada; `none` nos
 * demais casos e com versão desconhecida.
 */
export function updateLevelFor(
  agentVersion: string | null,
  minimumVersion: string | null,
  recommendedVersion: string | null,
  protocolSupported = true,
): AgentUpdateLevel {
  const version = parseAgentVersion(agentVersion);
  if (!version) return 'none';
  if (!protocolSupported && isBelow(version, recommendedVersion ?? minimumVersion)) return 'mandatory';
  if (isBelow(version, minimumVersion)) return 'mandatory';
  if (isBelow(version, recommendedVersion)) return 'recommended';
  return 'none';
}

export interface LevelCounts {
  mandatory: number;
  recommended: number;
  /** Computadores sem versão informada ou fora do formato: ficam sem aviso. */
  unknown: number;
}

/**
 * Quantos computadores do canal ficam em cada nível com a mínima e a recomendada dadas. Os de
 * protocolo recusado (`refusedProtocolAgentVersions`, parte dos de `agentVersions`) contam como a
 * API os trata: obrigatórios sempre que a versão-alvo for maior que a deles.
 */
export function countLevels(
  agentVersions: AgentReleaseChannel['agentVersions'],
  minimumVersion: string | null,
  recommendedVersion: string | null,
  refusedProtocolAgentVersions: AgentReleaseChannel['refusedProtocolAgentVersions'] = [],
): LevelCounts {
  const refused = new Map<string | null, number>();
  for (const { version, agents } of refusedProtocolAgentVersions) {
    refused.set(version, (refused.get(version) ?? 0) + agents);
  }

  const counts: LevelCounts = { mandatory: 0, recommended: 0, unknown: 0 };
  const add = (level: AgentUpdateLevel, agents: number) => {
    if (level === 'mandatory') counts.mandatory += agents;
    else if (level === 'recommended') counts.recommended += agents;
  };
  for (const { version, agents } of agentVersions) {
    if (!parseAgentVersion(version)) {
      counts.unknown += agents;
      continue;
    }
    const refusedAgents = Math.min(refused.get(version) ?? 0, agents);
    add(updateLevelFor(version, minimumVersion, recommendedVersion, false), refusedAgents);
    add(updateLevelFor(version, minimumVersion, recommendedVersion), agents - refusedAgents);
  }
  return counts;
}

/**
 * As versões do manifesto que a tela oferece como mínima ou recomendada do canal.
 *
 * A API decide pelo `selectable`, e a tela confere de novo a regra da assinatura para não
 * oferecer o que um agente assinado recusaria (`verification_failed`): versão sem assinatura
 * nunca no estável, e no piloto só enquanto nenhuma versão assinada tiver saído nele.
 */
export function selectableVersions(release: Pick<AgentReleaseChannel, 'channel' | 'versions'>): AgentReleaseVersion[] {
  const anySigned = release.versions.some((v) => v.signed);
  return release.versions.filter(
    (v) => v.selectable && (v.signed || (release.channel === 'pilot' && !anySigned)),
  );
}
